import { TypographyCaption } from '@heidi/design-system/typography';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';

import {
  MAX_PATIENTS_WITH_DETAILS,
  type DeletePatientProfilesApprovalFields,
} from './delete-patient-profiles-approval';
import { LabelledContextPanel } from './labelled-context-panel';

/**
 * Clinician-facing body for the ``delete_patient_profiles`` HITL approval card.
 * The tool input carries only profile ids, so the page resolves each patient's
 * name and linked sessions and renders them through `renderPatient` with
 * `DeletePatientRow`. Only the first `MAX_PATIENTS_WITH_DETAILS` patients are
 * resolved so a bulk delete cannot fan out unbounded reads.
 */
const MAX_SESSIONS_SHOWN = 10;

const shortId = (id: string): string => `…${id.slice(-8)}`;

export interface DeletePatientSessionRow {
  session_id?: string | null;
  session_name?: string | null;
  session_gist?: string | null;
  created_at?: string | null;
}

export type DeletePatientRowStatus = 'loading' | 'error' | 'ready';

export interface DeletePatientRowProps {
  patientProfileId: string;
  name: string | null | undefined;
  sessions: DeletePatientSessionRow[] | undefined;
  status: DeletePatientRowStatus;
  deleteLinkedSessions: boolean | undefined;
}

const SessionRow = ({ session }: { session: DeletePatientSessionRow }) => {
  const intl = useIntl();
  const label =
    session.session_name?.trim() ||
    session.session_gist?.trim() ||
    intl.formatMessage({ defaultMessage: 'Untitled session', id: 'VZOiVMXn0z' });

  return (
    <li className="flex flex-col gap-0.5">
      <span className="break-words text-sm leading-snug text-text-primary [overflow-wrap:anywhere]">
        {label}
      </span>
      {session.created_at && (
        <span className="text-xs leading-snug text-text-secondary">
          {intl.formatDate(new Date(session.created_at), {
            dateStyle: 'medium',
            timeStyle: 'short',
          })}
        </span>
      )}
    </li>
  );
};

export const DeletePatientRow = ({
  patientProfileId,
  name,
  sessions: rawSessions,
  status,
  deleteLinkedSessions,
}: DeletePatientRowProps) => {
  const intl = useIntl();

  const displayName =
    name?.trim() ||
    intl.formatMessage({
      defaultMessage: 'Unnamed patient',
      id: '3xsM7qRobj',
      description: 'Delete patient approval card: fallback when a patient has no name',
    });
  const sessions = [...(rawSessions ?? [])].sort(
    (a, b) =>
      (b.created_at ? new Date(b.created_at).getTime() : 0) -
      (a.created_at ? new Date(a.created_at).getTime() : 0)
  );
  const shown = sessions.slice(0, MAX_SESSIONS_SHOWN);
  const hiddenCount = sessions.length - shown.length;

  const sessionsLabel =
    deleteLinkedSessions === false
      ? intl.formatMessage(
          {
            defaultMessage:
              '{count, plural, one {# session} other {# sessions}} kept and unlinked',
            id: 'Z175xdYw0T',
            description:
              'Delete patient approval card: caption above sessions that are kept but unlinked',
          },
          { count: sessions.length }
        )
      : deleteLinkedSessions === true
        ? intl.formatMessage(
            {
              defaultMessage:
                '{count, plural, one {# session} other {# sessions}} deleted',
              id: 'iivMkgg7pE',
              description:
                'Delete patient approval card: caption above sessions deleted with the patient',
            },
            { count: sessions.length }
          )
        : intl.formatMessage(
            {
              defaultMessage:
                '{count, plural, one {# linked session} other {# linked sessions}}',
              id: 'GbRGQ55qS0',
              description:
                'Delete patient approval card: caption above linked sessions when the tool did not say whether they are deleted',
            },
            { count: sessions.length }
          );

  return (
    <li
      className="flex flex-col gap-1.5"
      data-testid={`delete-patient-row-${patientProfileId}`}
    >
      <span className="break-words text-sm font-medium leading-snug text-text-primary [overflow-wrap:anywhere]">
        {displayName}
        <span className="ms-1.5 font-mono text-xs font-normal text-text-secondary">
          {shortId(patientProfileId)}
        </span>
      </span>
      {status === 'loading' ? (
        <TypographyCaption className="text-text-secondary">
          {intl.formatMessage({
            defaultMessage: 'Loading sessions…',
            id: 'cVJdIM+sgo',
            description: 'Delete patient approval card: linked sessions are loading',
          })}
        </TypographyCaption>
      ) : status === 'error' ? (
        <TypographyCaption className="text-validation-destructive">
          {intl.formatMessage({
            defaultMessage: "Couldn't load this patient's sessions",
            id: 'h1xXVQX6Bp',
            description:
              'Delete patient approval card: linked sessions failed to load, so the list is unknown',
          })}
        </TypographyCaption>
      ) : sessions.length === 0 ? (
        <TypographyCaption className="text-text-secondary">
          {intl.formatMessage({
            defaultMessage: 'No linked sessions',
            id: 'E5+plyP5SQ',
            description: 'Delete patient approval card: patient has no linked sessions',
          })}
        </TypographyCaption>
      ) : (
        <div className="flex flex-col gap-1 border-s border-border ps-3">
          <TypographyCaption className="font-medium text-text-secondary">
            {sessionsLabel}
          </TypographyCaption>
          <ul className="flex flex-col gap-1">
            {shown.map((session, index) => (
              <SessionRow key={session.session_id ?? index} session={session} />
            ))}
          </ul>
          {hiddenCount > 0 && (
            <TypographyCaption className="text-text-secondary">
              {intl.formatMessage(
                {
                  defaultMessage: '+{count} more',
                  id: 'RqWNCawvHe',
                  description:
                    'Delete patient approval card: count of sessions not listed individually',
                },
                { count: hiddenCount }
              )}
            </TypographyCaption>
          )}
        </div>
      )}
    </li>
  );
};

export interface DeletePatientProfilesApprovalDetailsProps {
  fields: DeletePatientProfilesApprovalFields;
  renderPatient: (patientProfileId: string) => ReactNode;
}

export const DeletePatientProfilesApprovalDetails = ({
  fields,
  renderPatient,
}: DeletePatientProfilesApprovalDetailsProps) => {
  const intl = useIntl();
  const { patientProfileIds, deleteLinkedSessions } = fields;
  const shownIds = patientProfileIds.slice(0, MAX_PATIENTS_WITH_DETAILS);
  const hiddenPatientCount = patientProfileIds.length - shownIds.length;

  return (
    <div className="flex flex-col gap-3" data-testid="delete-patient-profiles-details">
      <p className="rounded-md border border-validation-warning/30 bg-validation-warning/10 px-3 py-2 text-xs leading-snug text-text-primary">
        {deleteLinkedSessions === false
          ? intl.formatMessage({
              defaultMessage:
                'These patients will be deleted. Their sessions are kept but will no longer be linked to a patient.',
              id: '722wS0HqYb',
              description: 'Delete patient approval card: banner when sessions are kept',
            })
          : deleteLinkedSessions === true
            ? intl.formatMessage({
                defaultMessage:
                  'These patients will be deleted, along with every session linked to them.',
                id: 'ifvPS7VOWz',
                description:
                  'Delete patient approval card: banner when sessions are deleted too',
              })
            : intl.formatMessage({
                defaultMessage: 'These patients will be deleted.',
                id: 'ycVDasd5ga',
                description:
                  'Delete patient approval card: banner when the tool did not say what happens to sessions',
              })}
      </p>

      <LabelledContextPanel
        label={intl.formatMessage(
          {
            defaultMessage:
              'Deleting {count, plural, one {# patient} other {# patients}}',
            id: 'DRygSE4QW/',
            description: 'Delete patient approval card: label above the patient list',
          },
          { count: patientProfileIds.length }
        )}
      >
        <ul className="flex flex-col gap-3">{shownIds.map((id) => renderPatient(id))}</ul>
        {hiddenPatientCount > 0 && (
          <TypographyCaption className="text-text-secondary">
            {intl.formatMessage(
              {
                defaultMessage:
                  '+{count, plural, one {# more patient} other {# more patients}}',
                id: 'p9+RS2L4wH',
                description:
                  'Delete patient approval card: count of patients whose details are not loaded',
              },
              { count: hiddenPatientCount }
            )}
          </TypographyCaption>
        )}
      </LabelledContextPanel>
    </div>
  );
};
